"use client";

import { useCallback, useEffect, useState } from "react";
import {
  desktopInstallGuide,
  type DesktopInstallGuide,
  type PwaPlatform,
} from "../pwa-install";
import { useDevicePlatform } from "./use-device-platform";
import { useStandaloneDisplay } from "./use-standalone-display";

export interface PwaBeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface UseDesktopPwaInstallResult {
  platform: PwaPlatform;
  /** A desktop browser that is not already running the installed site. */
  canInstall: boolean;
  guide: DesktopInstallGuide | null;
  canNativeInstall: boolean;
  promptNativeInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

export function useDesktopPwaInstall(): UseDesktopPwaInstallResult {
  const { platform, isNativeShell } = useDevicePlatform();
  const isStandalone = useStandaloneDisplay();
  const [nativePrompt, setNativePrompt] =
    useState<PwaBeforeInstallPromptEvent | null>(null);
  const [installedThisSession, setInstalledThisSession] = useState(false);
  const guide = desktopInstallGuide(platform);

  useEffect(() => {
    const handleBeforeInstall = (event: Event) => {
      event.preventDefault();
      setNativePrompt(event as PwaBeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      setInstalledThisSession(true);
      setNativePrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const promptNativeInstall = useCallback(async () => {
    if (!nativePrompt) {
      return "unavailable" as const;
    }

    await nativePrompt.prompt();
    const { outcome } = await nativePrompt.userChoice;
    setNativePrompt(null);
    if (outcome === "accepted") {
      setInstalledThisSession(true);
    }
    return outcome;
  }, [nativePrompt]);

  return {
    platform,
    canInstall:
      guide !== null && !isNativeShell && !isStandalone && !installedThisSession,
    guide,
    canNativeInstall: nativePrompt !== null && guide?.canUseNativePrompt === true,
    promptNativeInstall,
  };
}
